import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Home, ArrowLeft, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const CreateAnuncioPage = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    expires_at: ''
  });

  // Calculate max date (30 days from now)
  const today = new Date();
  const maxDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const minDateStr = today.toISOString().split('T')[0];
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Debes iniciar sesión para publicar');
      navigate('/auth');
      return;
    }

    if (!formData.title || !formData.description || !formData.category || !formData.location) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // If no expiry date selected, default to 30 days
      const expiresAt = formData.expires_at 
        ? new Date(formData.expires_at).toISOString()
        : maxDate.toISOString();

      const response = await fetch(`${API}/anuncios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          expires_at: expiresAt
        })
      });

      if (response.ok) {
        toast.success('¡Anuncio enviado! Será revisado por un moderador antes de publicarse.');
        navigate('/mis-anuncios');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear el anuncio');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear el anuncio');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Inicia sesión</h2>
            <p className="text-slate-600 mb-4">Necesitas una cuenta para publicar anuncios</p>
            <Link to="/auth">
              <Button className="bg-red-600 hover:bg-red-700">Iniciar sesión</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} className="hidden sm:flex">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Button>
              <Button variant="ghost" onClick={() => navigate('/anuncios')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anuncios
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Publicar Anuncio</h1>
        <p className="text-slate-600 mb-6">Tu anuncio será revisado antes de publicarse (normalmente en menos de 24h).</p>

        {/* Rules reminder */}
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700">
                <p className="font-medium mb-1">Recuerda las normas:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>No incluyas datos de contacto (email, teléfono) en el anuncio</li>
                  <li>Los interesados te contactarán por mensaje privado</li>
                  <li>Máximo 30 días de duración</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Categoría *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ofrecer-servicios">🔧 Ofrezco servicios (soy profesional)</SelectItem>
                    <SelectItem value="buscar-servicios">🔍 Busco servicios (necesito ayuda)</SelectItem>
                    <SelectItem value="otros">📝 Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Título del anuncio *</Label>
                <Input
                  id="title"
                  placeholder="Ej: Fisioterapeuta con experiencia en deportes"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  maxLength={100}
                />
                <p className="text-xs text-slate-500">{formData.title.length}/100 caracteres</p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe tu servicio o lo que necesitas. Incluye experiencia, disponibilidad, zona de trabajo, etc. NO incluyas email ni teléfono."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="min-h-[150px]"
                  maxLength={2000}
                />
                <p className="text-xs text-slate-500">{formData.description.length}/2000 caracteres</p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Ubicación / Zona *</Label>
                <Input
                  id="location"
                  placeholder="Ej: Zúrich, Ginebra, Toda Suiza..."
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  maxLength={50}
                />
              </div>

              {/* Expiry Date */}
              <div className="space-y-2">
                <Label htmlFor="expires_at">Fecha de expiración (opcional)</Label>
                <Input
                  id="expires_at"
                  type="date"
                  min={minDateStr}
                  max={maxDateStr}
                  value={formData.expires_at}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                />
                <p className="text-xs text-slate-500">Si no seleccionas fecha, el anuncio durará 30 días</p>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/anuncios')}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar para revisión
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateAnuncioPage;
